/**
 * WillYouEnjoyItCard — Feb 2026 restructure to a horizontal,
 * tube-map-styled reading experience.
 *
 * Layout per row:
 *   ●─ 1 Living the course              [ 2-col essay paragraphs ] [ pull-quote gutter ]
 *   ●─ 2 Living at the university       [ 2-col essay paragraphs ] [ pull-quote gutter ]
 *   ●─ 3 Living in {city}               [ 2-col essay paragraphs ] [ pull-quote gutter ]
 *
 * Design notes:
 *   • Each row leads with a tube-style numbered station (large
 *     coloured ring + big numeral) rendered as SVG so the halo /
 *     line-cap style matches JourneyRail.
 *   • Essay body sits in cols 1-2 of a 3-col grid (`md:col-span-2`);
 *     column 3 holds a pull-quote extracted from the essay so
 *     parents skim-scan a punchy line without reading the whole
 *     paragraph.
 *   • Pull-quote picker: prefer the second sentence if 40-180 chars,
 *     else the longest sentence in that window, else the first
 *     sentence, else nothing (renders as an empty gutter — no ugly
 *     "quote coming soon" line).
 *   • Data source unchanged: `degree.cluster5_narrative` from the
 *     `wua-cluster5-generator` supervisor job.
 *   • Renders `null` when the payload is missing or all three
 *     essays are empty.
 */
import { MapPin, School, BookOpen, Quote } from "lucide-react";

const STATION_COLOURS = {
  1: "#7C3AED", // WUA violet — course
  2: "#14B8A6", // teal — university
  3: "#F59E0B", // amber — city
};

function pickPullQuote(text) {
  if (!text) return null;
  const clean = text.replace(/\s+/g, " ").trim();
  // Split on sentence terminators while keeping the punctuation.
  const sentences = clean.match(/[^.!?]+[.!?]+/g) || [clean];
  const inRange = sentences.filter((s) => {
    const len = s.trim().length;
    return len >= 40 && len <= 180;
  }).map((s) => s.trim());
  if (inRange.length === 0) return null;
  // Prefer the second sentence if it's in-range (more descriptive
  // than a common opener like "MIT is not a campus that…").
  if (sentences[1]) {
    const s2 = sentences[1].trim();
    if (s2.length >= 40 && s2.length <= 180) return s2;
  }
  // Otherwise pick the longest in-range sentence for a fuller line.
  return inRange.sort((a, b) => b.length - a.length)[0];
}

function TubeStation({ n, colour, label }) {
  const R = 22;
  return (
    <div className="flex min-w-0 items-center gap-3" data-testid={`wyei-station-${n}`}>
      <svg className="shrink-0" width={R * 2 + 8} height={R * 2 + 8} viewBox={`0 0 ${R * 2 + 8} ${R * 2 + 8}`} aria-hidden>
        <circle cx={R + 4} cy={R + 4} r={R} fill="white" stroke={colour} strokeWidth={4} />
        <text
          x={R + 4}
          y={R + 4}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={20}
          fontWeight={700}
          className="font-underground"
          fill={colour}
        >
          {n}
        </text>
      </svg>
      <div className="flex min-w-0 flex-col">
        <span className="text-[11px] font-underground-sc tracking-[0.15em] text-stone-500">
          Chapter {n}
        </span>
        <span className="font-editorial text-lg leading-tight text-stone-900 break-words">
          {label}
        </span>
      </div>
    </div>
  );
}

function EssayBody({ text }) {
  if (!text) return null;
  const paragraphs = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="font-editorial text-[15px] text-stone-800 leading-[1.7] space-y-4 max-w-prose break-words">
      {paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
}

function PullQuote({ text, colour }) {
  if (!text) return null;
  return (
    <blockquote
      className="relative border-l-4 pl-4 py-1 italic text-stone-700 text-[15px] leading-snug font-editorial"
      style={{ borderColor: colour }}
    >
      <Quote className="h-3.5 w-3.5 mb-1 opacity-60" style={{ color: colour }} aria-hidden />
      <span>{text}</span>
    </blockquote>
  );
}

function EnjoyRow({ n, colour, Icon, label, body, testKey, isLast }) {
  if (!body) return null;
  const pullQuote = pickPullQuote(body);
  return (
    <div
      data-testid={`wyei-row-${testKey}`}
      className={`grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 ${isLast ? "" : "pb-10 mb-10 border-b border-stone-200"}`}
    >
      <div className="min-w-0 md:col-span-2">
        <div className="flex min-w-0 items-center gap-3 mb-5">
          <TubeStation n={n} colour={colour} label={label} />
          <Icon className="hidden md:block h-5 w-5 text-stone-300 ml-1" aria-hidden />
        </div>
        <EssayBody text={body} />
      </div>
      <aside className="min-w-0 md:pl-2 flex items-start" data-testid={`wyei-row-${testKey}-pullquote`}>
        <PullQuote text={pullQuote} colour={colour} />
      </aside>
    </div>
  );
}

export default function WillYouEnjoyItCard({ degree, universityName, cityName }) {
  const n = degree?.cluster5_narrative;
  if (!n) return null;

  const course = (n.course_essay || "").trim();
  const uni = (n.university_essay || "").trim();
  const city = (n.city_essay || "").trim();
  if (!course && !uni && !city) return null;

  // New ordering: 1 course → 2 university → 3 city (living inward-out).
  const rows = [
    {
      key: "course",
      n: 1,
      colour: STATION_COLOURS[1],
      icon: BookOpen,
      label: "Living the course",
      body: course,
    },
    {
      key: "uni",
      n: 2,
      colour: STATION_COLOURS[2],
      icon: School,
      label: `Living at ${universityName || "the university"}`,
      body: uni,
    },
    {
      key: "city",
      n: 3,
      colour: STATION_COLOURS[3],
      icon: MapPin,
      label: `Living in ${cityName || "the city"}`,
      body: city,
    },
  ].filter((r) => r.body);

  return (
    <section
      data-testid="will-you-enjoy-it-card"
      className="mt-6 min-w-0 rounded-lg border border-stone-200 bg-white p-4 sm:p-6 lg:p-8"
    >
      <div className="text-[10px] font-underground-sc tracking-[0.18em] text-[#4C1D95] mb-6">
        Will you enjoy it?
      </div>
      {rows.map((r, i) => (
        <EnjoyRow
          key={r.key}
          n={r.n}
          colour={r.colour}
          Icon={r.icon}
          label={r.label}
          body={r.body}
          testKey={r.key}
          isLast={i === rows.length - 1}
        />
      ))}
    </section>
  );
}
